"use client";

import React from "react";
import { ArrowRight, FlaskConical, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wpQueryParams = {
  product_cat: "16",
  per_page: 6,
  order: "desc",
  orderby: "date"
};
const wpQuery = new WP_Query(wpQueryParams);
function PeptidesConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="flex flex-col rounded-md border border-border bg-card p-5 shadow-xs animate-pulse">
            <div className="aspect-square w-full rounded-sm bg-muted" />
            <div className="mt-4 h-4 w-24 rounded bg-muted" />
            <div className="mt-2 h-6 w-3/4 rounded bg-muted" />
            <div className="mt-2 h-4 w-1/2 rounded bg-muted" />
            <div className="mt-6 flex items-center justify-between pt-4 border-t border-border">
              <div className="h-6 w-20 rounded bg-muted" />
              <div className="h-9 w-28 rounded bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-md border border-border bg-muted/40 p-8 text-center">
        <p className="text-sm font-medium text-muted-foreground">
          Unable to load laboratory catalogue at this moment.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
          Retry connection
        </Button>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="rounded-md border border-border bg-muted/30 p-8 text-center">
        <FlaskConical className="mx-auto size-8 text-muted-foreground stroke-[1.75]" />
        <p className="mt-2 text-sm text-muted-foreground font-default">
          No peptides currently listed in this category.
        </p>
        <Button asChild variant="outline" size="sm" className="mt-4">
          <Link to="/ShopPage">Browse complete inventory</Link>
        </Button>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const purityText = product.customFields?.purity || (product.attributes?.find(a => a.name.toLowerCase().includes("purity"))?.terms[0]?.name ?? "≥99.0% HPLC");
      const molecularWeight = product.customFields?.molecular_weight || (product.attributes?.find(a => a.name.toLowerCase().includes("molecular weight"))?.terms[0]?.name ?? "Lyophilized solid");
      return <article key={product.id} data-index={i} className="group relative flex flex-col justify-between rounded-md border border-border bg-card p-5 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary hover:shadow-md">
            <div>
              {/* Product Vial Image Area */}
              <div className="relative aspect-square w-full overflow-hidden rounded-sm bg-muted flex items-center justify-center p-6">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(207_85%_33%/0.06)_0%,transparent_70%)]" />
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="relative z-10 h-full w-full object-contain transition-transform duration-200 group-hover:scale-105" loading="lazy" decoding="async" /> : <div className="relative z-10 flex flex-col items-center justify-center text-muted-foreground">
                    <FlaskConical className="size-12 stroke-[1.5]" />
                    <span className="mt-2 text-xs font-mono uppercase tracking-wider">
                      Specimen Vial
                    </span>
                  </div>}
                {/* Purity Verification Ribbon */}
                <div className="absolute top-3 left-3 z-20">
                  <span className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-muted px-2 py-0.5 font-mono text-xs font-semibold text-primary">
                    <ShieldCheck className="size-3 shrink-0 stroke-[2]" />
                    {purityText}
                  </span>
                </div>
              </div>

              {/* Technical Metadata & Title */}
              <div className="mt-4 space-y-1.5">
                <p className="font-mono text-xs text-muted-foreground tracking-wide">
                  MW: {molecularWeight}
                </p>
                <h3 className="font-default text-lg font-semibold text-card-foreground leading-snug">
                  <Link to={product.link || "/ShopPage"} className="hover:text-primary transition-colors duration-150">
                    {product.title}
                  </Link>
                </h3>

                {product.short_description ? <div className="line-clamp-2 text-xs text-muted-foreground font-default leading-relaxed [&_p]:m-0" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} /> : <p className="line-clamp-2 text-xs text-muted-foreground font-default leading-relaxed">
                    Research-use-only synthetic peptide verified by analytical spectrometry and certified batch assay.
                  </p>}
              </div>
            </div>

            {/* Price & Add to Cart Transaction Bar */}
            <div className="mt-6 flex items-center justify-between gap-3 border-t border-border pt-4">
              <div className="flex flex-col">
                <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                  Unit Price
                </span>
                <ProductPrice product={product} className="font-default text-base font-bold text-foreground" />
              </div>

              <div className="shrink-0 w-36">
                <AddToCartButton product={product} productId={product.id} variant="default" size="sm" text="Add to Cart" showQuantity={false} className="w-full" buttonClassName="bg-primary text-primary-foreground font-medium text-xs tracking-wide h-8 w-full rounded-md shadow-xs hover:bg-primary/90 transition-colors" />
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Peptides() {
  return <section id="featured-peptides" data-nav="light" className="bg-background py-14 md:py-20 text-foreground">
      <div className="mx-auto max-w-[1088px] px-6">
        {/* Section Header */}
        <div className="mb-10 flex flex-col justify-between gap-4 border-b border-border pb-6 md:flex-row md:items-end">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-border bg-muted font-mono text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                SYNTHETIC CHAINS
              </Badge>
              <span className="h-px w-8 bg-border" />
              <span className="font-mono text-[11px] text-muted-foreground">
                RUO SPECIFICATION
              </span>
            </div>
            <h2 className="font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Featured Research Peptides
            </h2>
            <p className="max-w-xl text-sm text-muted-foreground font-default">
              High-purity lyophilized peptides validated via HPLC and mass spectrometry for institutional laboratory research in India.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" size="default" className="group border-border text-foreground hover:border-primary hover:text-primary transition-[color,border-color] duration-150">
              <Link to="/ShopPage">
                <span>See All Peptides</span>
                <ArrowRight className="size-4 transition-transform duration-150 group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Dynamic WooCommerce Catalog */}
        <EcommerceProductsProvider wp_query={{
        product_cat: [16],
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <PeptidesConsumer />
        </EcommerceProductsProvider>

        {/* Technical Footer Compliance Notice */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2 font-mono">
            <span className="inline-block size-2 rounded-full bg-primary" />
            <span>BATCH DOCUMENTATION: COA &amp; HPLC TRACES SUPPLIED WITH DISPATCH</span>
          </div>
          <span className="font-mono text-[11px] uppercase tracking-wide">
            Cold-Chain Validated Delivery
          </span>
        </div>
      </div>
    </section>;
}